import { useCallback, useEffect, useLayoutEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import { createPortal } from 'react-dom'

/**
 * El selector de color del editor, en el panel: el cuadro de saturación y luz,
 * la tira del tono y el campo HEX.
 *
 * Sustituye al `<input type="color">`, que en cada sistema abre una cosa
 * distinta —en macOS, la rejilla de colores de Apple con «Show Colors…»— y no
 * deja escribir un código. Es el mismo modelo que `ColorSquarePicker` del
 * editor, sin la transparencia (una paleta no la usa) y con eventos de puntero,
 * que valen igual para el ratón que para el dedo.
 */
type HSV = { h: number; s: number; v: number }

function hsvToHex({ h, s, v }: HSV): string {
  const f = (n: number) => {
    const k = (n + h / 60) % 6
    return v - v * s * Math.max(0, Math.min(k, 4 - k, 1))
  }
  const toHex = (x: number) => Math.round(x * 255).toString(16).padStart(2, '0')
  return `#${toHex(f(5))}${toHex(f(3))}${toHex(f(1))}`.toUpperCase()
}

function hexToHsv(hex: string): HSV {
  const m = hex.match(/^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i)
  if (!m) return { h: 0, s: 0, v: 1 }
  const r = parseInt(m[1]!, 16) / 255
  const g = parseInt(m[2]!, 16) / 255
  const b = parseInt(m[3]!, 16) / 255
  const max = Math.max(r, g, b)
  const d = max - Math.min(r, g, b)
  let h = 0
  if (d !== 0) {
    if (max === r) h = ((g - b) / d + 6) % 6
    else if (max === g) h = (b - r) / d + 2
    else h = (r - g) / d + 4
    h *= 60
  }
  return { h, s: max === 0 ? 0 : d / max, v: max }
}

/** `#abc` o `abc` → `#AABBCC`; lo que no sea un color, nulo. */
export function normalizeHex(raw: string): string | null {
  const value = raw.trim().replace(/^#/, '')
  if (/^[0-9a-f]{6}$/i.test(value)) return `#${value.toUpperCase()}`
  if (/^[0-9a-f]{3}$/i.test(value)) return `#${value.split('').map((c) => c + c).join('').toUpperCase()}`
  return null
}

/** Un arrastre con el puntero: al bajar y mientras se mueve, la posición dentro del elemento. */
function useDrag(onMove: (x: number, y: number, rect: DOMRect) => void) {
  const ref = useRef<HTMLDivElement>(null)
  const onPointerDown = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return
    event.preventDefault()
    const el = ref.current
    if (!el) return
    el.setPointerCapture(event.pointerId)
    const move = (clientX: number, clientY: number) => {
      const rect = el.getBoundingClientRect()
      onMove(clientX - rect.left, clientY - rect.top, rect)
    }
    move(event.clientX, event.clientY)
    const onPointerMove = (ev: PointerEvent) => move(ev.clientX, ev.clientY)
    const stop = () => {
      el.removeEventListener('pointermove', onPointerMove)
      el.removeEventListener('pointerup', stop)
      el.removeEventListener('pointercancel', stop)
    }
    el.addEventListener('pointermove', onPointerMove)
    el.addEventListener('pointerup', stop)
    el.addEventListener('pointercancel', stop)
  }, [onMove])
  return { ref, onPointerDown }
}

export function ColorPickerBody({ value, onChange }: { value: string; onChange: (hex: string) => void }) {
  const [hsv, setHsv] = useState<HSV>(() => hexToHsv(normalizeHex(value) ?? '#FFFFFF'))
  const [draft, setDraft] = useState(() => (normalizeHex(value) ?? value).toUpperCase())
  /* Lo que viene de fuera (otra paleta, deshacer) mueve el cuadro; lo que sale
     de aquí no, o el tono saltaría a 0 al llevar la saturación a cero. */
  const emitted = useRef(value)
  useEffect(() => {
    if (value === emitted.current) return
    emitted.current = value
    const hex = normalizeHex(value)
    if (hex) { setHsv(hexToHsv(hex)); setDraft(hex) }
  }, [value])

  const emit = (next: HSV) => {
    setHsv(next)
    const hex = hsvToHex(next)
    emitted.current = hex
    setDraft(hex)
    onChange(hex)
  }
  const square = useDrag(useCallback((x: number, y: number, rect: DOMRect) => {
    emit({ ...hsv, s: Math.max(0, Math.min(1, x / rect.width)), v: Math.max(0, Math.min(1, 1 - y / rect.height)) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hsv.h]))
  const hue = useDrag(useCallback((x: number, _y: number, rect: DOMRect) => {
    emit({ ...hsv, h: Math.max(0, Math.min(360, (x / rect.width) * 360)) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hsv.s, hsv.v]))

  const hueColor = hsvToHex({ h: hsv.h, s: 1, v: 1 })
  const solid = hsvToHex(hsv)

  return (
    <div className="dash-color-picker">
      <div ref={square.ref} className="dash-color-picker-square" style={{ background: hueColor }} onPointerDown={square.onPointerDown}>
        <span className="dash-color-picker-thumb" style={{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%`, background: solid }} />
      </div>
      <div ref={hue.ref} className="dash-color-picker-hue" onPointerDown={hue.onPointerDown}>
        <span className="dash-color-picker-thumb is-hue" style={{ left: `${(hsv.h / 360) * 100}%`, background: hueColor }} />
      </div>
      <label className="dash-color-picker-hex">
        <span className="dash-color-picker-hex-chip" style={{ background: solid }} aria-hidden="true" />
        <span className="dash-color-picker-hex-label">HEX</span>
        <input
          value={draft}
          spellCheck={false}
          autoComplete="off"
          maxLength={7}
          onChange={(event) => {
            const next = event.target.value.toUpperCase()
            setDraft(next)
            const hex = normalizeHex(next)
            /* Al escribir, sólo con los seis dígitos: con tres, «#ABC» saltaría
               a «#AABBCC» mientras aún se escribe. */
            if (hex && next.replace('#', '').length === 6) {
              emitted.current = hex
              setHsv(hexToHsv(hex))
              onChange(hex)
            }
          }}
          onBlur={() => {
            const hex = normalizeHex(draft)
            if (!hex) { setDraft(solid); return }
            setDraft(hex)
            if (hex !== emitted.current) { emitted.current = hex; setHsv(hexToHsv(hex)); onChange(hex) }
          }}
          onKeyDown={(event) => { if (event.key === 'Enter') (event.target as HTMLInputElement).blur() }}
        />
      </label>
    </div>
  )
}

/**
 * El selector en una ventanita pegada a lo que lo abrió: debajo si cabe, si no
 * encima. Se cierra con un clic fuera o con Escape.
 */
export function ColorPickerPopover({ anchor, value, onChange, onClose, label }: {
  anchor: HTMLElement | null
  value: string
  onChange: (hex: string) => void
  onClose: () => void
  label?: string
}) {
  const popRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)

  useLayoutEffect(() => {
    const place = () => {
      const pop = popRef.current
      if (!anchor || !pop) return
      const rect = anchor.getBoundingClientRect()
      const width = pop.offsetWidth
      const height = pop.offsetHeight
      const below = rect.bottom + 8
      const top = below + height <= window.innerHeight - 8 ? below : Math.max(8, rect.top - height - 8)
      const left = Math.min(Math.max(8, rect.left + rect.width / 2 - width / 2), window.innerWidth - width - 8)
      setPos({ top, left })
    }
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [anchor])

  useEffect(() => {
    const onDown = (event: PointerEvent) => {
      const target = event.target as Node
      if (popRef.current?.contains(target) || anchor?.contains(target)) return
      onClose()
    }
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose() }
    document.addEventListener('pointerdown', onDown, true)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onDown, true)
      document.removeEventListener('keydown', onKey)
    }
  }, [anchor, onClose])

  return createPortal(
    <div
      ref={popRef}
      className="dash-color-popover"
      role="dialog"
      aria-label={label}
      style={pos ? { top: pos.top, left: pos.left } : { top: -9999, left: -9999 }}
    >
      <ColorPickerBody value={value} onChange={onChange} />
    </div>,
    document.body,
  )
}
